'use client'

// Where a click on him lands. Sam, 3 Nov: "Perhaps he needs a carrying interface, when you click on Pino - it shows a
// popup like a minecraft inventory - and shows what he is carrying".
//
// He is one instance of a skinned mesh drawn sixteen at a time, so there is nothing of his own to click. This is a box
// about his size, never drawn, that does not follow him frame by frame: its raycast puts it where he is drawn this frame
// (birdPose, written by the bridge) and only then tests the ray. Nothing here runs per frame, and a ray is cast only
// when somebody presses on the world or lets go, and, with a mouse while the pointer is free, as it moves (for the hand
// over him).
//
// Two ways in, because walking takes the mouse. With the pointer free (a phone, or a mouse before the world has taken
// it), react-three's own events: a press and a let go on him, close together, is a tap, and opens his bag. With the
// pointer taken, a press reports wherever the mouse was when it was taken, not the crosshair, so those are ignored here
// and the walker casts its own ray from the middle of the screen (FirstPersonController, aimsAtBird).
//
// The bubble over his head (ThoughtBubble.jsx) is the sign that there is something to open, so it takes the same taps,
// the same crosshair and the same hand as he does: what follows is shared with it.

import { useEffect, useState } from 'react'
import * as THREE from 'three'
import useGameStore from '../../../stores/gameStore'
import { birdPose } from '../../../lib/world/birdPose'

// bird.glb at the scale the instances draw (1): 1.08 across, 2.18 tall, from his tail at z -0.7 to the tip of his beak
// at 1.9. The box stops short of the beak's tip so that somebody stood right in front of his face (the walker keeps
// 1.05 from his middle) is never inside it, where a ray would find nothing.
const HIT_BOX = new THREE.BoxGeometry(1.1, 2.2, 2.0).translate(0, 1.1, 0.3)
const HIT_MATERIAL = new THREE.MeshBasicMaterial()

// a tap, not a drag that happened to end on him: how far a press may travel and how long it may last
const TAP_PX = 10
const TAP_MS = 600

/** The box once mounted, the bubble over his head once mounted, the press in progress, and when a press on him last
 * opened the bag. */
export const birdHit = { mesh: null, bubble: null, down: null, tookAt: 0 }

/** Whether a click that has just come in was the tap that opened his bag, so the walker does not also take the mouse. */
export function tookBird() {
  return performance.now() - birdHit.tookAt < 700
}

/** The views that hide the HUD, or have the camera somewhere else, where his bag would open behind them. */
export function elsewhere(s = useGameStore.getState()) {
  return !!(s.droneActive || s.photoMode || s.pinoCam || s.vendingOpen || s.jobsOpen)
}

/** Open his bag, from a click on him or the crosshair on him. */
export function openBag() {
  if (elsewhere()) return false
  birdHit.tookAt = performance.now()
  useGameStore.getState().setBagOpen(true)
  return true
}

// Made once and reused, so a click allocates nothing.
const ray = new THREE.Raycaster()
const CENTRE = new THREE.Vector2(0, 0)
const hits = []

/**
 * How far away he is under the crosshair, or the bubble over his head while it shows, or null when the crosshair is on
 * neither. A raycast at each, on a click.
 */
export function aimsAtBird(camera) {
  if (!camera) return null
  ray.setFromCamera(CENTRE, camera)
  hits.length = 0
  birdHit.mesh?.raycast(ray, hits)
  // (its own raycast answers only while it shows)
  birdHit.bubble?.raycast(ray, hits)
  let near = null
  for (let i = 0; i < hits.length; i++) if (near === null || hits[i].distance < near) near = hits[i].distance
  return near
}

// How far off a click on him still counts. Further than this he is a speck, and a click there was meant for the
// world: with no limit, a first click to start walking, aimed at a building with him somewhere behind it, opened his
// bag instead (the review of 3 Nov). Both the pointer and the crosshair go through here.
const REACH = 30

// The raycast the box answers with: moved onto him first, then the ordinary mesh test, then anything past REACH dropped.
function onHim(raycaster, intersects) {
  if (!birdPose.seen) return
  this.position.set(birdPose.x, birdPose.y, birdPose.z)
  this.rotation.set(0, birdPose.rotY, 0)
  this.updateMatrix()
  this.updateMatrixWorld()
  const before = intersects.length
  THREE.Mesh.prototype.raycast.call(this, raycaster, intersects)
  for (let i = intersects.length - 1; i >= before; i--) if (intersects[i].distance > REACH) intersects.splice(i, 1)
}

const locked = () => typeof document !== 'undefined' && !!document.pointerLockElement
// a mouse that can hover, and not taken for walking: only then is there a cursor to turn into a hand
const canHover = () => typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)')?.matches

/** A press on him, or on the bubble over his head, with the pointer free. */
export function pressDown(e) {
  if (locked()) return
  const p = e.nativeEvent
  birdHit.down = { id: p.pointerId, x: p.clientX, y: p.clientY, at: performance.now() }
}

/** Its let go: close to the press in time and place, it was a tap, and opens what `open` opens (his bag). */
export function pressUp(e, open = openBag) {
  const d = birdHit.down
  const p = e.nativeEvent
  birdHit.down = null
  if (locked() || !d || d.id !== p.pointerId) return
  if (performance.now() - d.at > TAP_MS) return
  if (Math.hypot(p.clientX - d.x, p.clientY - d.y) > TAP_PX) return
  e.stopPropagation()
  open()
}

const HAND = { over: () => { document.body.style.cursor = 'pointer' }, out: () => { document.body.style.cursor = '' } }
const NO_HAND = { over: undefined, out: undefined }

/** The pointer as a hand over him, while there is a mouse that can hover and walking has not taken it. */
export function useHand() {
  const [hover, setHover] = useState(() => canHover() && !locked())
  useEffect(() => {
    if (!canHover()) return undefined
    const onLock = () => setHover(!locked())
    document.addEventListener('pointerlockchange', onLock)
    return () => {
      document.removeEventListener('pointerlockchange', onLock)
      document.body.style.cursor = ''
    }
  }, [])
  // the hand goes as soon as the mouse is taken, wherever it was
  useEffect(() => { if (!hover) document.body.style.cursor = '' }, [hover])
  return hover ? HAND : NO_HAND
}

export function BirdHit() {
  const hand = useHand()
  return (
    <mesh
      ref={(m) => { birdHit.mesh = m || null }}
      name="bird_hit"
      geometry={HIT_BOX}
      material={HIT_MATERIAL}
      visible={false}
      matrixAutoUpdate={false}
      // never drawn, so never compiled: the boot pass shows hidden things to build them (effects/Precompiler.jsx)
      userData={{ noPrecompile: true }}
      raycast={onHim}
      onPointerDown={pressDown}
      onPointerUp={pressUp}
      onPointerOver={hand.over}
      onPointerOut={hand.out}
    />
  )
}

export default BirdHit
